import React, { useCallback, useEffect, useState } from "react";
import "../Quiz/Quiz.css";
import "./PlateGame.css";
import { Seo } from "../../Shared/Seo/Seo.tsx";
import { IntroLead, IntroRest } from "../../Landing/IntroParagraphs.tsx";
import { findBySlug, quizRoutes } from "../../Shared/Seo/routeSeo.ts";
import { Operations } from "../../MathFactsGenerator/Constants.ts";
import { SettingsProvider } from "../../MathFactsGenerator/Settings/SettingsStore.tsx";
import { ValueRange } from "../../MathFactsGenerator/Settings/ValueRange.tsx";
import { canBuildDeck, flashCardSettings } from "../deck.ts";
import { createQuizQueue, QuizQueue } from "../Quiz/quizQueue.ts";
import { factKey, recordCorrect, recordMiss } from "../Quiz/missedFacts.ts";
import { MissedFactsPractice } from "../Quiz/MissedFactsPractice.tsx";
import { track } from "../../Shared/analytics.ts";
import { PlateGameRun, PlateResult } from "./PlateGameRun.tsx";
import { PlateGameSummary } from "./PlateGameSummary.tsx";
import { PlateLeaderboard } from "./PlateLeaderboard.tsx";
import { Mode } from "./plateGame.ts";
import {
    addScore,
    clearLeaderboard,
    Leaderboard,
    qualifies,
    readLastName,
    readLeaderboard,
    today,
} from "./plateLeaderboard.ts";
import { platesGameEventParams } from "./gameEventParams.ts";
import { FactPicker, operationsOf, signFor } from "./FactPicker.tsx";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

interface PlateGamePageProps {
    slug: string;
    operation: Operations;
}

interface Summary {
    result: PlateResult;
    /** Whether the name box is still showing. */
    canSave: boolean;
    savedRank: number | null;
}

/**
 * Slingshot Plate Smash (ADR 0060): a keyword landing page around a carnival
 * booth on the quiz's queue and missed-facts store, with a leaderboard kept in
 * this browser beside it. A quiz route carrying `game: "plates"`; its `quiz`
 * is only the operation the fact picker opens on.
 */
export function PlateGamePage({ slug, operation }: PlateGamePageProps) {
    const entry = findBySlug(slug)!;
    const t = useTranslation();
    const [settings, setSettings] = useState(() => flashCardSettings(operation));
    const [mode, setMode] = useState<Mode | null>(null);
    const [queue, setQueue] = useState<QuizQueue | null>(null);
    const [runId, setRunId] = useState(0);
    const [summary, setSummary] = useState<Summary | null>(null);
    const [missesVersion, setMissesVersion] = useState(0);
    const [playedOperations, setPlayedOperations] = useState<string[]>([]);

    // Read after mount, so the prerendered page is the same for everyone.
    const [board, setBoard] = useState<Leaderboard>({ easy: [], hard: [] });
    const [boardMode, setBoardMode] = useState<Mode>("easy");
    const [highlight, setHighlight] = useState<{ mode: Mode; rank: number } | null>(null);
    const [lastName, setLastName] = useState("");
    useEffect(() => {
        setBoard(readLeaderboard());
        setLastName(readLastName());
    }, []);

    const ready = canBuildDeck(settings);
    const playing = mode !== null && queue !== null && summary === null;
    const plainQuiz = quizRoutes().find((route) => route.quiz === operation && !route.game);

    function start(chosen: Mode) {
        // Built here from settings as they stand, never during render: the
        // generator is non-deterministic (ADR 0001 §4).
        setQueue(createQuizQueue(settings));
        setPlayedOperations(operationsOf(settings));
        setMode(chosen);
        setBoardMode(chosen);
        setHighlight(null);
        setSummary(null);
        setRunId((id) => id + 1);
    }

    function changeMode() {
        setMode(null);
        setQueue(null);
        setSummary(null);
    }

    const handleFinish = useCallback(
        (result: PlateResult) => {
            // A wrong plate is a miss, recorded once per problem; a first-shot
            // hit clears like a right quiz answer (§7).
            result.misses.forEach((miss) => recordMiss(factKey(miss.front)));
            result.correctFronts.forEach((front) => recordCorrect(factKey(front)));
            setMissesVersion((v) => v + 1);
            // No name, no score beyond the counts: ADR 0060 §7.
            track("quiz_completed", platesGameEventParams(playedOperations, result));
            const current = readLeaderboard();
            setBoard(current);
            setSummary({ result, canSave: qualifies(current, result.mode, result.score), savedRank: null });
        },
        [playedOperations]
    );

    function save(name: string) {
        if (!summary) {
            return;
        }
        const { mode: played, score } = summary.result;
        const facts = playedOperations.map(signFor).join(" ");
        const { board: next, rank } = addScore(played, { name, score, facts, date: today() });
        setBoard(next);
        setBoardMode(played);
        setLastName(readLastName());
        setHighlight(rank >= 0 ? { mode: played, rank } : null);
        setSummary({ ...summary, canSave: false, savedRank: rank >= 0 ? rank : null });
    }

    return (
        <SettingsProvider settings={settings} setSettings={setSettings}>
            <Seo entry={entry} />
            <div className="landingContent">
                <h1>{entry.h1}</h1>
                <IntroLead paragraphs={entry.intro} />
            </div>

            <div className="plateLayout">
                <div className="quizTool plateMain">
                    {summary ? (
                        <PlateGameSummary
                            key={runId}
                            result={summary.result}
                            canSave={summary.canSave}
                            savedRank={summary.savedRank}
                            initialName={lastName}
                            onSave={save}
                            onSkip={() => setSummary({ ...summary, canSave: false })}
                            onPlayAgain={() => start(summary.result.mode)}
                            onChangeMode={changeMode}
                        />
                    ) : playing ? (
                        <PlateGameRun key={runId} queue={queue!} mode={mode!} onFinish={handleFinish} />
                    ) : (
                        <div className="quizModePicker plateIntro">
                            <p className="plateIntroHow">{t("flashCards.platesHowToPlay")}</p>
                            <p className="quizModePrompt">{t("flashCards.platesWhichFacts")}</p>
                            <FactPicker settings={settings} setSettings={setSettings} label={t("flashCards.platesWhichFacts")} />
                            <p className="quizModePrompt">{t("flashCards.platesChooseMode")}</p>
                            {ready ? (
                                <div className="plateModes">
                                    <button type="button" onClick={() => start("easy")}>
                                        <span className="plateModeName">{t("flashCards.platesEasy")}</span>
                                        <span className="plateModeHint">{t("flashCards.platesEasyHint")}</span>
                                    </button>
                                    <button type="button" onClick={() => start("hard")}>
                                        <span className="plateModeName">{t("flashCards.platesHard")}</span>
                                        <span className="plateModeHint">{t("flashCards.platesHardHint")}</span>
                                    </button>
                                </div>
                            ) : (
                                <p className="quizEmpty">{t("flashCards.gameEmptyNote")}</p>
                            )}
                        </div>
                    )}

                    {!playing && plainQuiz && (
                        <p className="plateCalmer">
                            {t("flashCards.gameCalmerPrefix")}
                            <a href={plainQuiz.slug}>{t("flashCards.gameCalmerLink")}</a>
                            {t("flashCards.gameCalmerSuffix")}
                        </p>
                    )}

                    {!playing && <MissedFactsPractice refreshKey={missesVersion} />}

                    {!playing && (
                        <details className="quizSettings">
                            <summary>{t("flashCards.changeWhatToPractise")}</summary>
                            <div className="valueRangeContainer">
                                <ValueRange RangeSettings={0} />
                                <ValueRange RangeSettings={1} />
                            </div>
                        </details>
                    )}
                </div>

                <PlateLeaderboard
                    board={board}
                    mode={boardMode}
                    onModeChange={setBoardMode}
                    highlight={highlight}
                    onClear={() => {
                        setBoard(clearLeaderboard());
                        setLastName("");
                        setHighlight(null);
                        if (summary) {
                            setSummary({ ...summary, savedRank: null });
                        }
                    }}
                />
            </div>

            <IntroRest paragraphs={entry.intro} />

            {entry.faq && entry.faq.length > 0 && (
                <section className="landingFaq">
                    <h2>{t("flashCards.frequentlyAskedQuestions")}</h2>
                    {entry.faq.map((item, i) => (
                        <div key={i} className="faqItem">
                            <h3>{item.q}</h3>
                            <p>{item.a}</p>
                        </div>
                    ))}
                </section>
            )}
        </SettingsProvider>
    );
}

export default PlateGamePage;
